<template>
    <DefaultLayout :channelCode="shortID">
        <template #nav-right>
            <!--   分享Link按鈕   -->
            <NavRightButton icon="share" :label="t('Tips.Icon.ShareLink')" @click="handleShareButtonOnClick" />
        </template>
        <template #content>
            <div class="panel-content">
                <!--左側面板-->
                <div class="left-panel">
                    <!--標題-->
                    <h2 class="title">
                        {{ t('Pages.SelectLanguage.Title') }}
                    </h2>
                    <!--會議標題-->
                    <div class="meet-title">
                        {{ meetTitle }}
                    </div>
                </div>
                <!--右側面板-->
                <div class="right-panel">
                    <div class="language-panel">
                        <SearchableLanguageList
                            :languages="translateLanguages"
                            :selected-locale="targetLang"
                            :search-placeholder="t('Pages.SelectLanguage.SearchPlaceholder')"
                            :empty-text="t('Pages.SelectLanguage.SearchEmpty')"
                            @select="handleLangOnClick"
                        />
                    </div>
                </div>

            </div>
            <!--   QR Code 談窗   -->
            <ShareQRCodeModal :isOpen="showQRCodeModal" @close="handleShareQRCodeModalOnClose" />
        </template>
    </DefaultLayout>
</template>
<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { findLanguageItemsBySupportAzureSTT } from '@shared-data'
import { useInfoStore } from '@shared-store'
import { NavRightButton, useLoadingAction } from '@shared-components'
import { ErrorCodeEnum, ResponseDTO, ShortUrl, ShortUrlRes, VoiceGenderTypeEnum } from '@shared-api'
import DefaultLayout from '../layouts/DefaultLayout.vue'
import ShareQRCodeModal from '../components/TutorialPage/ShareQRCodeModal.vue'
import SearchableLanguageList from '../components/common/SearchableLanguageList.vue'
import { useRouter } from 'vue-router'
import { HttpResponse, isEmpty, stringIsEmpty } from '@ipevo-rd/service-web-utils'
import { useI18n } from 'vue-i18n'

/**
 * 比對兩個字串陣列是否完全相同，含順序；供 languages / unsupported_tts_languages 判斷是否需要更新
 *
 * @param a {string[]} 第一個陣列
 * @param b {string[]} 第二個陣列
 * @returns {boolean} 長度與每個位置的元素皆相同時為 true
 *
 * @example
 * arraysEqualInOrder(['en-US', 'ja-JP'], ['ja-JP', 'en-US']) // false，順序不同視為不同
 */
const arraysEqualInOrder = (a: string[], b: string[]): boolean =>
	a.length === b.length && a.every((value, index) => value === b[index])

export default defineComponent({
    name: 'LanguagePage',
    components: { ShareQRCodeModal, DefaultLayout, SearchableLanguageList, NavRightButton },
    setup() {
        // Router
        const router = useRouter()
        // 取得會議名稱 / 該會議支持的翻譯語言列表 / 目前選擇的目標語言 / 設定目標語言 / Session Short ID
        const infoStore = useInfoStore()
        const {
          meetTitle,
          supportedTranslationLanguages,
          unsupportedTTSLanguages,
          targetLang,
          shortID,
          sessionID,
          targetGender,
        } = storeToRefs(infoStore)
        const {
          setTargetLang,
          setMeetTitle,
          setSupportedTranslationLanguages,
          setUnsupportedTTSLanguages,
          setSessionID,
          setTargetGender,
        } = infoStore
        // 使用 i18n
        const { t } = useI18n()


        // 是否顯示QR Code modal
        const showQRCodeModal = ref(false)
		// 是否仍停留在本頁；離頁後 refreshChannelInfo 晚到的回應不准再彈 toast 或導頁
		let isPageActive = true
        // 該會議支持的翻譯語言列表
        const translateLanguages = computed(() => findLanguageItemsBySupportAzureSTT(supportedTranslationLanguages.value))


        /**
         * 點擊分享按鈕
         * @return {void}
         */
        const handleShareButtonOnClick = (): void => {
            // 將分享的Modal設定為顯示
            showQRCodeModal.value = true
        }

        /**
         * 關閉 QR Code modal
         * @return {void}
         */
        const handleShareQRCodeModalOnClose = (): void => {
            // 將 QR Code modal 關閉
            showQRCodeModal.value = false
        }

        // 選語言後進逐字稿的 Loading 流程：toast、擋重複點、導頁完成後收掉
        const { isLoading, run: runEnterChannel } = useLoadingAction('Loading.Entering', () => (
            router.push({ name: 'tutorial', params: { shortID: shortID.value } })
        ))

        /**
         * 點擊語言按鈕
         * @param locale {string} 語言的 locale
         * @return {void}
         */
        const handleLangOnClick = (locale: string): void => {
            // Loading 中不再接受選擇
            if (isLoading.value) {
                return
            }

			// 使用者已主動選定語言，提前標記離頁，避免 push 與 unmount 間的窄 race 讓晚到的 refreshChannelInfo 回應誤彈窗或導頁
			isPageActive = false
            // 設定目標語言
            setTargetLang(locale)
            // 進入 Loading 流程跳轉至 Tutorial 頁面
            runEnterChannel()
        }

		/**
		 * 頻道已失效時的收尾處理：顯示錯誤訊息並導離首頁；已離頁時不彈窗也不導頁
		 * @param errorMessage {string} 顯示在 toast 上的錯誤訊息
		 * @return {void}
		 */
		const goHomeAsInvalid = (errorMessage: string): void => {
			// 使用者已經離開本頁，晚到的回應不准再彈窗或導頁
			if (!isPageActive) {
				return
			}
			// 顯示錯誤訊息
			window.$toast?.({ type: 'error', message: errorMessage })
			// 跳轉回首頁
			router.push({ name: 'home' })
		}

		/**
		 * 進頁時重打取得最新頻道資訊，依比對結果更新 store 或導離；非同步呼叫，不阻塞掛載流程
		 * @return {void}
		 */
		const refreshChannelInfo = (): void => {
			ShortUrl.getOriginalURL({
				url: shortID.value,
			}).then((res: HttpResponse) => {
				// 取得代碼 / 資料 / 訊息
				const { code, data, message } = res as unknown as ResponseDTO
				// 正確取得到結果後
				if (code === ErrorCodeEnum.Success && !isEmpty(data)) {
					// 取得資料
					const {
						id,
						genders,
						languages,
						title,
						unsupported_tts_languages,
						size,
						capacity,
					} = data as unknown as ShortUrlRes
					// 人數已達上限，頻道即將被導離，不需要再比對任何欄位
					if (size >= capacity) {
						goHomeAsInvalid(t('Tips.Error.Channel.LimitExceeded'))
						// 不繼續往下執行
						return
					}

					// 缺席的標題對齊 HomePage 的 title ?? '' 轉換
					const nextMeetTitle = title ?? ''
					// 缺席的不支援 TTS 語言視為空陣列，代表全語言皆支援
					const nextUnsupportedTTSLanguages = unsupported_tts_languages ?? []
					// 取第一筆發音性別，缺席或空陣列皆 fallback 成預設女聲，對齊 HomePage 的取值邏輯
					const nextTargetGender = genders?.[0] || VoiceGenderTypeEnum.Female
					// store 是共用狀態，即使使用者已離頁，晚到的欄位更新照套也無害
					// 會議標題有變動才更新
					if (nextMeetTitle !== meetTitle.value) {
						setMeetTitle(nextMeetTitle)
					}

					// 支援翻譯語言有變動才更新（含順序）
					if (!arraysEqualInOrder(languages, supportedTranslationLanguages.value)) {
						setSupportedTranslationLanguages(languages)
					}

					// 不支援TTS語言有變動才更新（含順序）
					if (!arraysEqualInOrder(nextUnsupportedTTSLanguages, unsupportedTTSLanguages.value)) {
						setUnsupportedTTSLanguages(nextUnsupportedTTSLanguages)
					}

					// Session ID有變動才更新，代表主辦端換了新 session
					if (id !== sessionID.value) {
						setSessionID(id)
					}

					// 目標發音性別有變動才更新
					if (nextTargetGender !== targetGender.value) {
						setTargetGender(nextTargetGender)
					}

					// 翻譯語言只剩一種，直接進 Tutorial 頁，不需要停留在語言選擇頁
					if (languages.length === 1) {
						// 使用者已經離開本頁，晚到的回應不准再設定目標語言或導頁
						if (!isPageActive) {
							return
						}

						// 設定目標語言
						setTargetLang(languages[0])
						// 跳轉至翻譯頁面
						router.push({ name: 'tutorial', params: { shortID: shortID.value } })
					}
				} else {
					// 發生錯誤時，不分錯誤碼，一律彈窗並導回首頁
					goHomeAsInvalid(message)
				}
			}).catch((error) => {
				// 網路層錯誤，可能只是暫時抖動，不把使用者踢離頁面，留待下次操作或重新整理再試，只記錄供排查
				console.warn('refreshChannelInfo network error', error)
			})
		}


        /**
         * 掛載時初始化
         * @return {void}
         */
        onMounted(() => {
            // 如果short ID不存在，則導向首頁
            if (stringIsEmpty(shortID.value)) {
                router.push({ name: 'home' })
			} else {
				// 重驗頻道資訊，確保 store 快取跟後端現況一致
				refreshChannelInfo()
            }
        })

        /**
         * 卸載時
         * @return {void}
         */
        onBeforeUnmount(() => {
			// 離頁，refreshChannelInfo 晚到的回應不准再彈 toast 或導頁
			isPageActive = false
        })


        return {
            t,
            meetTitle,
            showQRCodeModal,
            translateLanguages,
            targetLang,
            shortID,
            handleShareButtonOnClick,
            handleShareQRCodeModalOnClose,
            handleLangOnClick,
        }
    },
})
</script>
<style scoped lang="scss">
@use '@shared-assets/scss/variables' as *;

// 語言面板桌機版最大寬度
$language-panel-max-width: 480px;

.panel-content {
    width: 100%;
    height: 100%;
    display: flex;

    .left-panel > * {
        margin: 0;
    }

    .title {
        color: var(--ui-grey-darkest);
        line-height: 1.2;
        font-weight: 600;
        letter-spacing: -0.1px;
        padding-bottom: 24px;
        width: 100%;
    }

    .meet-title {
        color: var(--ui-grey-darkest);
        line-height: 1.2;
        font-weight: 300;
        letter-spacing: -0.1px;
        width: 100%;
    }
}

// 900 以上：左右分欄
@media screen and (min-width: $breakpoint-md) {
    .panel-content {
        .left-panel,
        .right-panel {
            height: 100%;
            min-width: 50%;
            flex: 1;
        }

        .left-panel {
            padding-left: 10vw;
            padding-right: 5vw;
            display: flex;
            flex-direction: column;
            justify-content: center;
        }

        .right-panel {
            display: flex;
            align-items: center;
            padding-right: 10vw;
        }

        .language-panel {
            width: 100%;
            max-width: $language-panel-max-width;
            height: 70vh;
            height: 70dvh;
            // 面板本身已經算好精確高度，清單吃滿面板高度即可
            --searchable-language-list-max-height: 100%;
        }
    }
}

// 標題與會議標題的四階字級
@media screen and (min-width: $breakpoint-xl) {
    .panel-content {
        .title { font-size: 75px; }
        .meet-title { font-size: 60px; }
    }
}

@media screen and (min-width: $breakpoint-lg) and (max-width: #{$breakpoint-xl - 1}) {
    .panel-content {
        .title { font-size: 56px; }
        .meet-title { font-size: 45px; }
    }
}

@media screen and (min-width: $breakpoint-sm) and (max-width: #{$breakpoint-lg - 1}) {
    .panel-content {
        .title { font-size: 30px; }
        .meet-title { font-size: 24px; }
    }
}

// 900 以下：上下排，會議標題放在大標下方、最多兩行
@media screen and (max-width: #{$breakpoint-md - 1}) {
    .panel-content {
        flex-direction: column;
        padding: 0;

        .left-panel {
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            min-height: 15vh;
            padding: 16px 5vw 8px;
        }

        .title {
            text-align: center;
            margin: 0;
            padding: 0;
            line-height: 1.2;
        }

        .meet-title {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            margin-top: 8px;
            text-align: center;
        }

        .right-panel {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 0;
            padding: 0 5vw;
        }

        .language-panel {
            width: 100%;
            height: 100%;
            // 右欄高度已由 flex 精算，清單吃滿面板高度即可
            --searchable-language-list-max-height: 100%;
        }
    }
}

@media screen and (max-width: #{$breakpoint-sm - 1}) {
    .panel-content {
        .title { font-size: 20px; }
        .meet-title { font-size: 16px; }
    }
}
</style>
